import { useUpdatingProxyNiceModal } from '@lessonup/client-integration';
import { FontLoader, PinViewerStatic } from '@lessonup/pin-renderer';
import {
  PlayerBarBottomPlaceholder,
  useCurrentPin,
  useCurrentPinAdjacentRawPinSlices,
  useCurrentStudents,
  useGetPinThemeById,
  useIsPlayerLoading,
  useNextPin,
  useNonNullAssignment,
  usePlayerDispatcher,
  usePlayerStore,
} from '@lessonup/players-modern';
import { color, colorSets, SpaceBetween, spacing, styled } from '@lessonup/ui-components';
import React from 'react';
import { PinViewerRealtimePlayer } from '../RealtimePlayer/dataComponents/PinViewerRealtimePlayer/PinViewerRealtimePlayer';
import { StudentJoinScreenModal } from '../RealtimePlayer/dataComponents/playerScreens/StudentJoinScreen/StudentJoinScreenModal';
import { useRealtimePlayerKeyboardEvents } from '../RealtimePlayer/hooks/useRealtimePlayerKeyboardEvents';
import {
  changeAllowNewPlayersAction,
  removeStudentFromAssignmentAction,
} from '../RealtimePlayer/utils/playerDispatcher.utils';
import { usePlayerTranslation } from '../hooks/usePlayerTranslation';
import { NextPinView } from './components/NextPinView';
import { PresenterNote } from './components/PresenterNote';
import { PresenterPlayerBottomHandler } from './components/PresenterPlayerBarBottomHandler';

export interface PresenterPlayerProps {
  onClose: () => void;
}

export const InitializedPresenterPlayer: React.FC<PresenterPlayerProps> = ({ onClose }) => {
  const { t } = usePlayerTranslation();
  const assignment = useNonNullAssignment();
  const currentPin = useCurrentPin();
  const getPinThemeById = useGetPinThemeById();
  const currentPinTheme = getPinThemeById(currentPin?.id);
  const nextPin = useNextPin();
  const nextPinTheme = getPinThemeById(nextPin?.id);
  const currentStudents = useCurrentStudents();
  const { setPlayerNavigationLock } = usePlayerStore();
  const dispatch = usePlayerDispatcher();
  const { previousPins, nextPins } = useCurrentPinAdjacentRawPinSlices(2);

  useRealtimePlayerKeyboardEvents(onClose);

  const allowNewPlayers = assignment.settings.status === 'open' && assignment.settings.playerSettings.allowNewPlayers; // Is this correct?
  const pincode = assignment.pincode ?? '';

  const studentModal = useUpdatingProxyNiceModal(
    StudentJoinScreenModal,
    {
      joinedStudents: currentStudents,
      pincode,
      allowNewPlayers,
      async handleAllowNewPlayers(allowNewPlayers: boolean) {
        dispatch(changeAllowNewPlayersAction(allowNewPlayers));
      },
      async handleRemoveStudent(studentId: string) {
        dispatch(removeStudentFromAssignmentAction(studentId));
      },
      onClose: () => {
        setPlayerNavigationLock(false);
      },
      t,
    },
    [currentStudents, allowNewPlayers]
  );

  const handleShowJoinScreenModal = () => {
    setPlayerNavigationLock(true);
    studentModal.show();
  };

  return (
    <StyledPresenterPlayerWrapper direction="x" spacing={spacing.size0}>
      {/* Loading prev|next pins to preload their static content. TODO virtualize these */}
      {previousPins.map((pin) => (
        <InvisiblePreview key={pin.id} pin={pin} pinTheme={getPinThemeById(pin.id)} />
      ))}
      <StyledMainContent direction="y" spacing={spacing.size16} alignItems="center" justifyContent="space-between">
        {currentPin && (
          <SpaceBetween
            direction="y"
            spacing={spacing.size24}
            alignItems="center"
            justifyContent="flex-start"
            style={{ flex: 1 }}
          >
            <StyledPinViewerWrapper>
              <FontLoader />
              <PinViewerRealtimePlayer pin={currentPin} withFAM={true} pinTheme={currentPinTheme} />
            </StyledPinViewerWrapper>
          </SpaceBetween>
        )}
        <PresenterPlayerBottomHandler showLabels={true} handleShowJoinScreenModal={handleShowJoinScreenModal} />
      </StyledMainContent>
      {nextPins.map((pin) => (
        <InvisiblePreview key={pin.id} pin={pin} pinTheme={getPinThemeById(pin.id)} />
      ))}
      <StyledSideContent direction="y" spacing={spacing.size16} padding={spacing.size16} justifyContent="space-between">
        <NextPinView nextPin={nextPin} pinTheme={nextPinTheme} />
        <PresenterNote noteContent={currentPin?.teacherSettings.notes} />
      </StyledSideContent>
    </StyledPresenterPlayerWrapper>
  );
};

export const PresenterPlayer: React.FC<PresenterPlayerProps> = ({ onClose }) => {
  const loading = useIsPlayerLoading();

  if (loading) {
    return (
      <StyledPresenterPlayerWrapper direction="x" spacing={spacing.size32}>
        <PlayerBarBottomPlaceholder minimized={true} mirrored={false} />
      </StyledPresenterPlayerWrapper>
    );
  }

  return <InitializedPresenterPlayer onClose={onClose} />;
};

const StyledPresenterPlayerWrapper = styled(SpaceBetween)`
  height: 100vh;
  width: 100%;
`;

const StyledMainContent = styled(SpaceBetween)`
  padding: ${spacing.size8};
  background: ${color.neutralNew.black.fill};
  flex: 1 1 auto;
  min-width: 0;
  position: relative;
  > * {
    width: 100%;
  }
`;

const StyledSideContent = styled(SpaceBetween)`
  background: ${colorSets.neutral.fillSubjacent};
  flex: 0 0;
  > * {
    width: 100%;
  }
`;

const StyledPinViewerWrapper = styled.div`
  width: 100%;
  flex: 80;
  min-width: 0;
  min-height: 0;
`;

const InvisiblePreview = styled(PinViewerStatic)`
  display: none;
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
`;
